import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatCheckboxChange } from '@angular/material';

import { ProcedureType, TerminalProcedure } from '../../airport/models/terminal-procedure.model';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';
import { formatBytes, isNumeric } from '../../util/helpers';
import { TreeNode } from './tree-node.model';


export interface ProcSelectionChangedEvent {
  selectedProcs: Array<TerminalProcedure>;
  selectedFiles: Set<string>;
  totalSize: number;
}


@Component({
  selector: 'app-proc-selection-tree',
  templateUrl: './proc-selection-tree.component.html',
  styleUrls: ['./proc-selection-tree.component.scss'],
  animations: [
    trigger('nodeExpansion', [
      state('collapsed', style({height: '0px', visibility: 'hidden', opacity: 0})),
      state('expanded', style({height: '*', visibility: 'visible', opacity: 1})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4,0.0,0.2,1)')),
    ])
  ]
})
export class ProcSelectionTreeComponent implements OnChanges {
  @Input() procedures: Array<TerminalProcedure>;
  @Input() airports: Array<AirportSearchResult>;
  @Output() selectionChanged = new EventEmitter<ProcSelectionChangedEvent>();

  selectedProcs: Array<TerminalProcedure>;
  selectedFiles: Set<string>;
  tree: TreeNode<void>;

  procTypes: Array<ProcedureType> = [
    ProcedureType.AirportDiagram,
    ProcedureType.IAP,
    ProcedureType.DP,
    ProcedureType.STAR,
    ProcedureType.TakeoffMinimums,
    ProcedureType.CHS
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.procedures && changes.procedures.currentValue) {
      this._initTree();
      this.selectionChanged.emit({
        selectedProcs: this.selectedProcs,
        selectedFiles: this.selectedFiles,
        totalSize: this.tree.selectedSize
      });
    }
  }

  private _initTree() {
    this.tree = new TreeNode<void>(
      null,
      null,
      this.procTypes.map(type => {
        const leaf = this._isLeafProc(type);
        return new TreeNode<ProcedureType>(
          type,
          TerminalProcedure.formatProcedureTypeFull(type, true),
          this.getAirportsByProcedureType(type).map(apt => new TreeNode<AirportSearchResult|TerminalProcedure>(
            leaf ? this._getLeafProc(apt.airportId, type) : apt,
            `${apt.identifier}: ${apt.name}`,
            leaf
              ? null
              : this.getProceduresAtAirport(type, apt.airportId).map(proc => new TreeNode<TerminalProcedure>(
              proc,
              proc.name,
              null,
              true
              )),
            leaf
          ))
        );
      })
    );
    this.selectedFiles = this.tree.getDescendantFiles();
    this.tree.updateTree(this.selectedFiles);
    this.selectedProcs = this.tree.getSelectedProcs();
  }

  isNodeSelected(node: TreeNode<any>): boolean {
    return node.selected === true;
  }

  isNodeIndeterminate(node: TreeNode<any>) {
    return node.selected === null;
  }

  onNodeChanged(node: TreeNode<any>, e: MatCheckboxChange) {
    const procs = node.getDescendantFiles();
    procs.forEach(proc => {
      if (e.checked) {
        this.selectedFiles.add(proc);
      }  else {
        this.selectedFiles.delete(proc);
      }
    });
    this.tree.updateTree(this.selectedFiles);
    this.selectedProcs = this.tree.getSelectedProcs();
    this.selectionChanged.emit({
      selectedProcs: this.selectedProcs,
      selectedFiles: this.selectedFiles,
      totalSize: this.tree.selectedSize
    });
  }

  toggleNodeExpanded(node: TreeNode<any>, event: MouseEvent) {
    node.expanded = !node.expanded;
    event.preventDefault();
  }

  getAirportsByProcedureType(type: ProcedureType): Array<AirportSearchResult> {
    const airportIds = (this.procedures || [])
      .filter(p => p.type === type)
      .map(p => p.airportId);
    return (this.airports || [])
      .filter(a => airportIds.includes(a.airportId))
      .sort((a1, a2) => a1.airportId - a2.airportId);
  }

  getProceduresAtAirport(type: ProcedureType, airportId: number): Array<TerminalProcedure> {
    return (this.procedures || []).filter(p => p.airportId === airportId && p.type === type);
  }

  formatSize(node: TreeNode<any>): string {
    const size = node.leaf ? node.size : node.selectedSize;
    return isNumeric(size) ? formatBytes(size) : null;
  }

  private _isLeafProc(type: ProcedureType): boolean {
    return type === ProcedureType.AirportDiagram || type === ProcedureType.CHS;
  }

  private _getLeafProc(airportId: number, type: ProcedureType): TerminalProcedure {
    return (this.procedures || [])
      .filter(proc => proc.airportId === airportId && proc.type === type)
      [0] || null;
  }
}
